import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Result } from "antd";
import { DownloadOutlined, CloseOutlined } from "@ant-design/icons";
import CustomButton from "../components/CustomButton";
import { PDFViewer } from "@ifa/ui";
import styles from "./OrderForm.module.css";

const FilePreview: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const url = searchParams.get("url");
  const type = searchParams.get("type"); // 'pdf' | 'image' | 'video'
  const name = searchParams.get("name") || "File Preview";

  const decodedUrl = useMemo(() => (url ? decodeURIComponent(url) : ""), [url]);
  const decodedName = useMemo(() => (name ? decodeURIComponent(name) : ""), [name]);

  const handleDownload = () => {
    if (decodedUrl) {
      const link = document.createElement("a");
      link.href = decodedUrl;
      link.download = decodedName || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleClose = () => {
    navigate(-1, { replace: true });
  };

  if (!decodedUrl) {
    return <Result status="404" title="No file URL provided" subTitle="Unable to preview file." />;
  }

  const renderContent = () => {
    if (type === "pdf" || decodedUrl.toLowerCase().endsWith(".pdf")) {
      return (
        <div style={{ height: "calc(100vh - 64px)", width: "100%" }}>
          <PDFViewer file={decodedUrl} />
        </div>
      );
    }

    if (type === "image" || /\.(jpe?g|png|gif|bmp|webp)$/i.test(decodedUrl) || decodedUrl.startsWith("data:image/")) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "calc(100vh - 64px)",
            padding: "20px",
            overflow: "auto",
          }}
        >
          <img src={decodedUrl} alt={decodedName} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
        </div>
      );
    }

    if (type === "video" || /\.(mp4|webm|ogg|mov)$/i.test(decodedUrl)) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "calc(100vh - 64px)",
            backgroundColor: "#000",
          }}
        >
          <video src={decodedUrl} controls style={{ maxWidth: "100%", maxHeight: "100%" }}>
            Your browser does not support the video tag.
          </video>
        </div>
      );
    }

    return (
      <Result
        status="info"
        title="Preview not available"
        subTitle={`File type not supported for preview: ${decodedName}`}
        extra={
          <CustomButton bgColor="#00C7B6" icon={<DownloadOutlined />} onClick={handleDownload}>
            Download File
          </CustomButton>
        }
      />
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", backgroundColor: "#f5f5f5" }}>
      <div
        style={{
          height: "64px",
          padding: "0 24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#fff",
          borderBottom: "1px solid #e8e8e8",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <div style={{ fontWeight: 500, fontSize: "16px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}>{decodedName}</div>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <CustomButton bgColor="transparent" icon={<DownloadOutlined />} onClick={handleDownload} />
          {/* Close button matching OrderForm style */}
          <div className={styles["header-close"]} onClick={handleClose} style={{ marginLeft: 16 }}>
            <CloseOutlined />
          </div>
        </div>
      </div>
      <div style={{ flex: 1, overflow: "hidden" }}>{renderContent()}</div>
    </div>
  );
};

export default FilePreview;
